import { useEffect } from "react";
import { motion } from "framer-motion";
import { Thermometer, Users, Music, ListChecks, Heart, ArrowLeft, Baby, Shell, Fish } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FAQ from "@/components/site/FAQ";
import FamilyInWater from "@/components/site/FamilyInWater";
import CommunityBenefits from "@/components/site/CommunityBenefits";
import { ClassLevels } from "@/components/site/ClassLevels";
import WhereNext from "@/components/site/WhereNext";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const babyLevels = [
  { icon: <Shell size={26} />, name: "Urchins", age: "8 – 12 weeks", note: "Your baby's very first, gentle introduction to the water.", accent: "#4FA3C6" },
  { icon: <Fish size={26} />, name: "Small Fry", age: "3 – 9 months", note: "Building trust and early water confidence through play.", accent: "#6BAFCC" },
  { icon: <Baby size={26} />, name: "Guppies", age: "9 – 18 months", note: "Growing curiosity and the first splashes of independence.", accent: "#1F7A8C" },
];

const nextItems = [
  { sub: "The next step up", label: "Tiddler & Pre-School Swimming", to: "/classes/toddler-swimming" },
  { sub: "See the full lesson pathway", label: "Our Swimming Lessons", to: "/classes" },
  { sub: "Check class availability", label: "Weekly Timetable", to: "/timetable" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const outcomes = [
  { icon: <Heart size={16} />, text: "Parent & baby bonding" },
  { icon: <Thermometer size={16} />, text: "Warm, welcoming pool" },
  { icon: <Baby size={16} />, text: "From 8 weeks old" },
];

const babyFaqs = [
  {
    q: "Can I swim with my baby before immunisation?",
    a: "Yes — current government guidance states that babies can swim prior to immunisations and there is no risk to the baby.",
  },
  {
    q: "What should my baby wear for their lessons?",
    a: "We require babies to wear 2 layers when swimming. This consists of an inner swim nappy (the absorbent layer) and an outer layer (the containment layer) — either a swim nappy with a good seal around the leg and waist, or a wetsuit that incorporates the nappy design.\n\nThe inner layer can be a disposable or reusable/washable swim nappy; the outer layer should fit snugly and create a seal around the waist and legs. You can purchase these at reception in our swim centre.",
  },
  {
    q: "How do adult-accompanied lessons work?",
    a: "Adult-accompanied lessons are available for tiddlers up to 4½ years. A parent or guardian gets in the pool with each child. These lessons provide just as much learning for the adult as they do for the child.",
  },
  {
    q: "Can both parents get in the pool?",
    a: "We encourage both parents to get into our Urchins and Small Fry classes — it's a lovely opportunity to bond as a family. As children get older, having more people in the pool becomes more of a distraction.",
  },
  {
    q: "What if my child is ill?",
    a: "Several infectious conditions mean your child shouldn't swim, including diarrhoea, sickness, a heavy cold or an ear infection.\n\nIf your little one has had an upset tummy or sickness, please wait until 48 hours have passed since the last episode before swimming. Always check with a medical/health practitioner if you need further guidance.",
  },
  {
    q: "My child has a medical condition — who should I inform?",
    a: "You'll complete a booking and medical form where you can disclose any known medical condition for your child or their adult swim companion. If anything changes while enrolled, please tell us immediately. If your child has a medical condition, please consult a doctor before starting a session or course.",
  },
  {
    q: "I cannot swim — is this a problem?",
    a: "Not at all. Our pool is 1.2m deep, so parents' and carers' feet can reach the ground. We aim to build the confidence of both parent and child, and our classes are relaxed and informative to help with exactly that.",
  },
];

const expect = [
  { icon: <Thermometer size={22} />, text: "Warm, welcoming pool environment" },
  { icon: <Users size={22} />, text: "Small classes with individual guidance" },
  { icon: <Music size={22} />, text: "Songs, gentle movements and play" },
  { icon: <ListChecks size={22} />, text: "Clear structure so you always know what's happening" },
];

export default function BabySwimming() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="baby-swimming-page">
      <Seo
        title="Baby Swimming Lessons (8 weeks–18 months) | Happy Tiddlers"
        description="Gentle, nurturing baby swimming lessons in Barton-le-Clay. Build water confidence and bond with your little one from 8 weeks old. Book today."
        path="/classes/baby-swimming"
      />
      <Navbar />

      {/* Header */}
      <section className="relative overflow-hidden pt-14 pb-20 md:pt-20 md:pb-24" data-testid="baby-header">
        <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <Link to="/classes" className="inline-flex items-center gap-2 text-[#0EA5E9] font-semibold hover:gap-3 transition-all mb-8" data-testid="baby-back-link">
            <ArrowLeft size={18} /> All classes
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Baby Swimming • 8 weeks – 18 months</span>
              <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
                A calm, positive first experience in the water
              </h1>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                Your baby's first swimming lessons should feel safe, gentle and enjoyable — for both of you.
              </p>
              <p className="mt-4 text-lg text-[#334155] leading-relaxed">
                At Happy Tiddlers, baby swimming is about building trust and confidence, not forcing outcomes.
                Babies move, explore and learn at their own pace, supported every step of the way.
              </p>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-9 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300"
                data-testid="baby-book-cta-top"
              >
                Book baby swimming lessons
              </a>

              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="baby-outcomes">
                {outcomes.map((o) => (
                  <span key={o.text} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-[#0F172A] shadow-sm" data-testid="baby-outcome">
                    <span className="text-[#0EA5E9]">{o.icon}</span>
                    {o.text}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 rotate-2 hover:rotate-0 transition-transform duration-500"
            >
              <img
                src="https://images.unsplash.com/photo-1726800820564-2eaecaa66b37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="A parent and baby enjoying a swimming lesson"
                className="w-full h-[380px] md:h-[520px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="relative z-10 -mt-10 md:-mt-16 pb-20 md:pb-24" data-testid="baby-expect">
        <div className="container mx-auto px-6 md:px-12">
          <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-slate-100 shadow-sm">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A]">What to expect</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {expect.map((e, i) => (
                <motion.div
                  key={e.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-4"
                  data-testid={`baby-expect-${i}`}
                >
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#0EA5E9] text-white shrink-0">{e.icon}</span>
                  <p className="text-lg text-[#334155] pt-2.5">{e.text}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 text-lg text-[#334155] leading-relaxed border-t border-slate-100 pt-8">
              Many parents are nervous before their first lesson. That's completely normal — and we're here to guide you through it.
            </p>
          </div>
        </div>
      </section>

      {/* Confidence before technique */}
      <section className="pb-24 md:pb-32" data-testid="baby-confidence">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-[2.5rem] overflow-hidden shadow-xl order-2 lg:order-1"
            >
              <img
                src="https://images.pexels.com/photos/32928609/pexels-photo-32928609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Father and baby bonding in the pool"
                className="w-full h-[380px] md:h-[460px] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-2"
            >
              <span className="inline-flex items-center gap-2 text-sm text-[#FF7A1A] tracking-[0.2em] uppercase font-bold">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#FF7A1A] text-white"><Heart size={18} /></span>
                Confidence before technique
              </span>
              <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
                Some babies splash. Some observe. Some cuddle in close. All of it is learning.
              </h2>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                Our instructors focus on helping babies feel relaxed and secure in the water,
                laying the foundation for confident swimming later on.
              </p>
              <p className="mt-6 font-heading text-xl font-semibold text-[#0A2540]">Positive first experiences matter.</p>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300"
                data-testid="baby-book-cta-bottom"
              >
                Book baby swimming lessons
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <FamilyInWater />

      <ClassLevels
        title="The stages your baby will grow through"
        intro="Little swimmers move up gently as they grow — always at their own pace."
        levels={babyLevels}
        accent="#0EA5E9"
      />

      <CommunityBenefits />

      <FAQ
        items={babyFaqs}
        intro="We know starting something new can feel daunting — especially for first-time parents. There's no such thing as a silly question, so here are the ones we're asked most."
      />

      <WhereNext items={nextItems} />

      <Footer />
    </div>
  );
}
